import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { APPLICATION_TYPE, ApplicationTypes, BannerModule, BASE_URL, BUILD_CREATION_TIMESTAMP, BUILD_VERSION_NUMBER, COMMON_URL, DateInterceptor, FingerprintingInterceptor, EdlCommonComponentsModule, EdlCommonServicesModule, EdlPageComponentsModule, EdlSiteMapModule, ErrorMessageHelpersService, GlobalComplyErrorHandler, HttpErrorHandler, NAVIGATION_MAP_PROVIDER, PagedResponseInterceptor, QuillUrlService, SessionService } from '@edl-development/edl-common';
import { PaetepNavigationMapProvider } from './services/navigation/paetep-navigation-map.provider';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ErrorTailorModule } from '@ngneat/error-tailor';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { UIRouterModule, UrlService } from '@uirouter/angular';
import { LocalStorageModule } from 'angular-2-local-storage';
import { LaddaModule } from 'angular2-ladda';
import isEqual from 'lodash-es/isEqual';
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { EdlPaetepComponent } from './edl-paetep.component';
import { EdlPaetepStates } from './edl-paetep.states';
import { EdlNgbDateParserFormatter } from './formatters/edl-ngb-date-parser.formatter';
import { LayoutModule } from './layout/layout.module';
import { LoginModule } from './login/login.module';
import { ModalModule } from './modals/modal.module';
import { ServiceModule } from './services/service.module';
import { ASSETS_URL } from './tokens/api.token';
import { VERSION } from './version';
import { EdlCfBuildType, EdlCfKeys, EdlCustomFormBuilderModule, EdlCustomFormDisplayModule, EdlCfErrorMessageHelpersService } from '@edl-development/edl-custom-forms';
import { PaetepCFDisplayEnhancementsModule } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-display/paetep-cf-display-enhancements.module';
import { PaetepCfDisplayContextInfoComponent } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-display/components/paetep-cf-display-context-info/paetep-cf-display-context-info.component';
import { PaetepCfContextInfoService } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-display/services/paetep-cf-context-info/paetep-cf-context-info.service';
import { PaetepCfKeysExtended } from './components/custom-form-enhancements/edl-cf-extensions/enums/paetep-cf-keys-extended.enum';
import { RegistrationModule } from './registration/registration.module';
import { PaetepCfDisplaySignatureComponent } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-display/components/paetep-cf-display-signature/paetep-cf-display-signature.component';
import { PaetepCfSignatureService } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-display/services/paetep-cf-signature/paetep-cf-signature.service';
import { PaetepCfBuilderContextInfoService } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-builder/services/paetep-cf-builder-context-info/paetep-cf-builder-context-info.service';
import { PaetepCfBuilderContextInfoComponent } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-builder/components/paetep-cf-builder-context-info/paetep-cf-builder-context-info.component';
import { PaetepCfBuilderSignatureComponent } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-builder/components/paetep-cf-builder-signature/paetep-cf-builder-signature.component';
import { PaetepCfBuilderSignatureService } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-builder/services/paetep-cf-builder-signature/paetep-cf-builder-signature.service';
import { CustomFormsMapperModule } from './services/custom-form-services/custom-forms-mapper.module';
import { PaetepCFBuilderEnhancementsModule } from './components/custom-form-enhancements/edl-cf-extensions/paetep-cf-builder/paetep-cf-builder-enhancements.module';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ReloadNotificationComponent } from './components/reload-notification/reload-notification.component';
import { QUILL_ROOT_CONFIG } from './quill/quill-config';
import { IS_PRINT_MODE } from './tokens/paetep-print.token';
import { AccessibleToastComponent } from './components/common-components/accessible-toast/accessible-toast.component';

export function JwtModuleConfigFactory() {
    const token = localStorage.getItem('edl-paetep.token');
    const parsed = token ? JSON.parse(token) : null;

    return parsed;
}

@NgModule({
    imports: [
        BrowserModule,
        HttpClientModule,
        NgbModule,
        LocalStorageModule.forRoot({
            prefix: 'edl-paetep',
            storageType: 'localStorage'
        }),
        JwtModule.forRoot({
            config: {
                tokenGetter: JwtModuleConfigFactory,
                allowedDomains: [environment.BASEDOMAIN, environment.BASEDOMAIN + environment.PORT]
            },
        }),
        UIRouterModule.forRoot({
            states: EdlPaetepStates,
            otherwise: {
                state: 'public.page-not-found',
                params: {}
            },
            useHash: false
        }),
        ServiceModule,
        LayoutModule,
        LoginModule,
        RegistrationModule,
        BrowserAnimationsModule,
        EdlCommonServicesModule,
        EdlCommonComponentsModule,
        EdlPageComponentsModule,
        LaddaModule.forRoot({
            style: 'zoom-in'
        }),
        ToastrModule.forRoot({
            toastComponent: AccessibleToastComponent,
            positionClass: 'toast-top-right',
            preventDuplicates: false,
            timeOut: 5000,
            closeButton: true,
            progressBar: true,
            enableHtml: false
        }),
        BsDropdownModule.forRoot(),
        LoadingBarModule,
        BannerModule,
        QuillModule.forRoot(QUILL_ROOT_CONFIG),
        ModalModule,

        ErrorTailorModule.forRoot({
            errors: {
                useFactory(
                    edlCommonService: ErrorMessageHelpersService,
                    edlCfService: EdlCfErrorMessageHelpersService
                ) {
                    return {
                        ...edlCommonService.generateErrorMap(),
                        ...edlCfService.generateErrorMap(),
                        invalidDate: () => 'Please enter a valid date.',
                        invalidOption: () => 'Please select a valid option.',
                        noEntries: () => 'At least one entry is required.',
                        groupCheckboxInvalid: () => 'Please select at least one option.',
                        emptyHtmlString: () => 'This field is required.',
                        nonUnique: () => 'This value must be unique.',
                        unchanged: () => 'Current value still selected. Please select another value.',
                        invalidFactor: () => 'Please enter a whole number between 10 and 30.',
                        invalidScore: () => 'Please enter a valid score (0–107).',
                        invalidRating: () => 'Please enter a valid rating (0–3).',
                        invalidNumber: () => 'Please enter a valid number.',
                    };
                },
                deps: [ErrorMessageHelpersService, EdlCfErrorMessageHelpersService]
            },
            blurPredicate: (element) => {
                return element.tagName === 'INPUT'
                    || element.tagName === 'SELECT'
                    || element.tagName === 'TEXTAREA'
                    || element.tagName === 'EDL-DATE-PICKER'
                    || element.tagName === 'EDL-DATE-RANGE'
                    || element.tagName === 'QUILL-EDITOR';
            }
        }),
        PaetepCFDisplayEnhancementsModule,
        EdlCustomFormDisplayModule.forRoot([
            {
                key: PaetepCfKeysExtended.CONTEXT_INFO,
                service: PaetepCfContextInfoService,
                component: PaetepCfDisplayContextInfoComponent,
            }, {
                key: PaetepCfKeysExtended.PAETEP_SIGNATURE,
                service: PaetepCfSignatureService,
                component: PaetepCfDisplaySignatureComponent
            }
        ]),
        PaetepCFBuilderEnhancementsModule,
        EdlCustomFormBuilderModule.forRoot([
            {
                key: PaetepCfKeysExtended.CONTEXT_INFO,
                config: { title: 'Contextual Info', icon: 'fas fa-newspaper', buildType: EdlCfBuildType.COMPONENT },
                service: PaetepCfBuilderContextInfoService,
                component: PaetepCfBuilderContextInfoComponent
            },
            {
                key: PaetepCfKeysExtended.PAETEP_SIGNATURE,
                config: { title: 'Signature', icon: 'fas fa-pencil-alt', buildType: EdlCfBuildType.COMPONENT, isSingleColumnOnly: true },
                service: PaetepCfBuilderSignatureService,
                component: PaetepCfBuilderSignatureComponent
            }
        ], [
            EdlCfKeys.SIGNATURE
        ]),
        CustomFormsMapperModule.forRoot(),
        EdlSiteMapModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    declarations: [
        EdlPaetepComponent,
        ReloadNotificationComponent,
        AccessibleToastComponent
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: FingerprintingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: PagedResponseInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorHandler, multi: true },
        { provide: APPLICATION_TYPE, useValue: ApplicationTypes.EDL_PAETEP },
        { provide: COMMON_URL, useValue: `${environment.BASEURI}/common` },
        { provide: BASE_URL, useValue: `${environment.BASEURI}/paetep` },
        { provide: ASSETS_URL, useValue: `${environment.ASSETSURI}/assets` },
        { provide: BUILD_VERSION_NUMBER, useValue: VERSION.version },
        { provide: BUILD_CREATION_TIMESTAMP, useValue: VERSION.timestamp },
        { provide: IS_PRINT_MODE, useValue: false },
        {
            provide: NgbDateParserFormatter,
            useFactory: () => new EdlNgbDateParserFormatter('longDate')
        },
        {
            provide: ErrorHandler,
            useClass: GlobalComplyErrorHandler
        },
        { provide: NAVIGATION_MAP_PROVIDER, useClass: PaetepNavigationMapProvider }
    ],
    bootstrap: [
        EdlPaetepComponent
    ]
})

export class EdlPaetepModule {
    constructor(
        private sessionService: SessionService,
        private urlService: UrlService,
        private quillUrlService: QuillUrlService
    ) {
        this.quillUrlService.registerUrlModule();
        if (this.sessionService.isRestorable()) {
            this.sessionService.restore();
        }

        urlService.config.type('myjson', {
            encode: (obj) => JSON.stringify(obj),
            decode: (str) => JSON.parse(str),
            is: (val) => typeof (val) === 'object',
            pattern: /[^/]+/,
            equals: (a, b) => isEqual(a, b),
        });


        this.urlService.config.type('searchBy', {
            encode: (obj) => {
                return obj;
            },
            decode: (obj) => {
                return decodeURIComponent(obj);
            },
            is: (val) => {
                return typeof val === 'string';
            },
            pattern: /[^/]*/,
            equals: (a, b) => isEqual(a, b),

        });
    }
}

